/**
 * Public API documentation at `/docs`.
 *
 * Replaces the raw `/api/mcp/manifest` JSON dump that we used to link to from
 * the landing footer / "For agents" section / FinalCTA. Same information, but
 * actually readable: organised into Overview → Auth → MCP → REST →
 * Client config → curl recipes.
 *
 * Design choices:
 *  - Live-fetches `/api/mcp/manifest` so the tool list stays in sync with
 *    the server (single source of truth for names, descriptions, schemas).
 *  - Hardcodes the REST surface — `/api/drafts`, `/api/send`, `/api/sends`,
 *    `/api/domains`, `/api/keys` — because there's no machine-readable
 *    schema for those yet and we want to curate which endpoints we expose
 *    publicly (e.g. `/api/auth/*` and `/api/billing/*` stay internal).
 *  - Public route, no auth — agents/devs need to read this before signing
 *    up.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Copy } from 'lucide-react';
import { Wordmark } from '@/components/Wordmark';
import { InstallButtons } from '@/components/install/InstallButtons';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Live MCP manifest types — narrow subset of what /api/mcp/manifest returns
// ---------------------------------------------------------------------------

interface JsonSchema {
  type?: string;
  description?: string;
  enum?: unknown[];
  items?: JsonSchema;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  default?: unknown;
}

interface ManifestTool {
  name: string;
  description: string;
  inputSchema: JsonSchema;
}

interface Manifest {
  protocolVersion: string;
  serverInfo: { name: string; version: string };
  tools: ManifestTool[];
}

// ---------------------------------------------------------------------------
// Section registry — drives the sidebar nav AND the in-page anchors
// ---------------------------------------------------------------------------

interface DocsSection {
  id: string;
  label: string;
}

const SECTIONS: DocsSection[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'auth', label: 'Authentication' },
  { id: 'mcp', label: 'MCP server' },
  { id: 'rest', label: 'REST endpoints' },
  { id: 'clients', label: 'Client setup' },
  { id: 'curl', label: 'curl recipes' },
  { id: 'errors', label: 'Errors & limits' },
];

// ---------------------------------------------------------------------------
// REST surface — curated list. Mirrors src/routes/*. We deliberately omit
// /api/auth, /api/billing, /api/me, /api/account, /api/admin from the public
// docs — those are SPA-only and not part of the supported API contract.
// ---------------------------------------------------------------------------

interface RestEndpoint {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  summary: string;
}

const REST_ENDPOINTS: Array<{ group: string; endpoints: RestEndpoint[] }> = [
  {
    group: 'Drafts',
    endpoints: [
      { method: 'GET', path: '/api/drafts', summary: 'List your drafts (most recently updated first).' },
      { method: 'POST', path: '/api/drafts', summary: 'Create an empty draft with an optional title.' },
      { method: 'GET', path: '/api/drafts/{id}', summary: 'Fetch a draft + its current version (HTML, design JSON, subject).' },
      { method: 'PATCH', path: '/api/drafts/{id}', summary: 'Update title / subject / preview text / from address / HTML.' },
      { method: 'DELETE', path: '/api/drafts/{id}', summary: 'Permanently delete a draft and all its versions.' },
    ],
  },
  {
    group: 'AI generation',
    endpoints: [
      { method: 'POST', path: '/api/generate', summary: 'Create a brand-new draft from a natural-language prompt.' },
      { method: 'POST', path: '/api/generate/modify', summary: 'Rewrite an existing draft from an instruction (whole-email or selected node).' },
    ],
  },
  {
    group: 'Sending',
    endpoints: [
      { method: 'POST', path: '/api/send', summary: 'Send the current version of a draft from a verified domain.' },
      { method: 'GET', path: '/api/sends', summary: 'List sent emails (immutable history).' },
    ],
  },
  {
    group: 'Domains',
    endpoints: [
      { method: 'GET', path: '/api/domains', summary: 'List your sending domains + verification status.' },
      { method: 'POST', path: '/api/domains', summary: 'Add a new domain — returns DNS records to publish.' },
      { method: 'POST', path: '/api/domains/{id}/verify', summary: 'Re-check DNS for a pending domain.' },
      { method: 'DELETE', path: '/api/domains/{id}', summary: 'Remove a domain.' },
    ],
  },
  {
    group: 'API keys (browser session only)',
    endpoints: [
      { method: 'GET', path: '/api/keys', summary: 'List your active and revoked API keys.' },
      { method: 'POST', path: '/api/keys', summary: 'Create a new key. Returned in plaintext once — store it.' },
      { method: 'DELETE', path: '/api/keys/{id}', summary: 'Revoke a key immediately.' },
    ],
  },
];

const METHOD_COLORS: Record<RestEndpoint['method'], string> = {
  GET: 'bg-success/10 text-success border-success/30',
  POST: 'bg-primary/10 text-primary border-primary/30',
  PATCH: 'bg-warn/10 text-warn border-warn/30',
  DELETE: 'bg-destructive/10 text-destructive border-destructive/30',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DocsPage() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [manifestErr, setManifestErr] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>(SECTIONS[0].id);

  useEffect(() => {
    document.title = 'API docs — SendFast';
  }, []);

  // Fetch the live MCP manifest. Fails silently into the error branch — the
  // page is still useful (REST + auth + curl) without it.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/mcp/manifest');
        if (!res.ok) throw new Error(`Manifest request failed (${res.status})`);
        const json = (await res.json()) as Manifest;
        setManifest(json);
      } catch (e) {
        setManifestErr(e instanceof Error ? e.message : 'Could not load manifest');
      }
    })();
  }, []);

  // IntersectionObserver to highlight the active sidebar item as the user
  // scrolls. We pick the section whose anchor is nearest the viewport top.
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visible = new Map<string, number>();

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            visible.set(s.id, entry.intersectionRatio);
          }
          // Active = first section with > 0 ratio, in DOM order.
          for (const cand of SECTIONS) {
            if ((visible.get(cand.id) ?? 0) > 0) {
              setActiveSection(cand.id);
              return;
            }
          }
        },
        { rootMargin: '-20% 0px -70% 0px', threshold: [0, 0.2, 0.6, 1] },
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <DocsHeader />

      <main className="mx-auto max-w-[1200px] px-6 pb-24 pt-10 lg:px-10">
        <header className="mb-12 max-w-2xl">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            SendFast API
          </h1>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
            Draft, modify, send, and list marketing emails programmatically. Two interchangeable
            surfaces — a <strong className="text-foreground">Model Context Protocol</strong>{' '}
            server for agents, and a plain <strong className="text-foreground">REST API</strong>{' '}
            for everything else. Same auth, same data.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[200px_minmax(0,1fr)]">
          <SidebarNav activeId={activeSection} />

          <div className="min-w-0 space-y-20">
            <OverviewSection />
            <AuthSection />
            <McpSection manifest={manifest} error={manifestErr} />
            <RestSection />
            <ClientsSection />
            <CurlSection />
            <ErrorsSection />
          </div>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function DocsHeader() {
  return (
    <header className="border-border bg-background/80 sticky top-0 z-30 border-b backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1200px] items-center justify-between px-6 lg:px-10">
        <div className="flex items-center gap-3">
          <Wordmark to="/" />
          <span className="text-muted-foreground hidden text-sm sm:inline">/ Docs</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link
            to="/app/settings?tab=api"
            className="text-muted-foreground hover:text-foreground transition"
          >
            Get an API key →
          </Link>
          <Button variant="secondary" size="sm" className="bg-foreground text-background hover:bg-foreground/90" asChild>
            <Link to="/sign-up">Sign up free</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

function SidebarNav({ activeId }: { activeId: string }) {
  return (
    <nav className="self-start lg:sticky lg:top-20">
      <p className="text-muted-foreground mb-3 text-[10px] font-medium uppercase tracking-[0.18em]">
        Contents
      </p>
      <ul className="space-y-1">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className={cn(
                'block rounded-md px-3 py-1.5 text-sm transition',
                activeId === s.id
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/60',
              )}
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Section: Overview
// ---------------------------------------------------------------------------

function OverviewSection() {
  return (
    <Section id="overview" title="Overview">
      <p>
        SendFast exposes two equivalent surfaces over the same data:
      </p>
      <ul className="my-4 space-y-3">
        <li>
          <strong className="text-foreground">MCP server</strong> at{' '}
          <Inline>POST /api/mcp</Inline> — drop-in for Claude Desktop, Cursor, LangGraph, or any
          MCP-aware agent. Discovery via <Inline>GET /api/mcp/manifest</Inline>.
        </li>
        <li>
          <strong className="text-foreground">REST API</strong> under <Inline>/api/*</Inline> —
          JSON in, JSON out. Same auth header, same rate limits, same resources.
        </li>
      </ul>
      <p>
        Pick whichever fits your stack. The 5 core capabilities — draft, modify, screenshot, send,
        list — exist on both. Domain management and API-key issuance currently live in REST only.
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: Auth
// ---------------------------------------------------------------------------

function AuthSection() {
  return (
    <Section id="auth" title="Authentication">
      <p>
        Every authenticated request — REST or MCP — uses an{' '}
        <Inline>Authorization: Bearer sf_live_...</Inline> header. Get a key from{' '}
        <Link to="/app/settings?tab=api" className="text-primary hover:underline">
          Settings → API keys
        </Link>{' '}
        (browser session required to mint one). The plaintext is shown once at creation —
        store it in your secrets manager.
      </p>
      <CodeBlock language="bash" code={`curl https://sendfast.ai/api/drafts \\
  -H 'Authorization: Bearer sf_live_xxxxxxxxxxxxxxxx'`} />
      <p className="text-muted-foreground text-sm">
        Keys are scoped to your account and inherit your tier's quotas. Revoke a leaked key
        from the same Settings page — revocation takes effect instantly.
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: MCP — live from manifest
// ---------------------------------------------------------------------------

function McpSection({ manifest, error }: { manifest: Manifest | null; error: string | null }) {
  return (
    <Section id="mcp" title="MCP server">
      <p>
        Point any MCP client at{' '}
        <Inline>POST https://sendfast.ai/api/mcp</Inline> (auth via Bearer header). The handshake
        is the standard <Inline>initialize</Inline> → <Inline>tools/list</Inline> →{' '}
        <Inline>tools/call</Inline> dance — full protocol details at{' '}
        <a
          href="https://modelcontextprotocol.io"
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          modelcontextprotocol.io
        </a>
        .
      </p>

      <div className="bg-muted/40 border-border my-6 rounded-md border p-4 text-sm">
        <p className="text-foreground font-medium">Manifest</p>
        <p className="text-muted-foreground mt-1">
          <Inline>GET /api/mcp/manifest</Inline> is public (no auth) and returns the live tool
          schemas. Everything below renders from that exact response.
        </p>
      </div>

      {error ? (
        <p className="text-destructive text-sm">Could not load live manifest: {error}</p>
      ) : !manifest ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-5">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="mt-3 h-3 w-2/3" />
              <Skeleton className="mt-2 h-3 w-1/2" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-muted-foreground text-sm">
            Protocol version <code className="text-foreground">{manifest.protocolVersion}</code>{' '}
            · Server <code className="text-foreground">{manifest.serverInfo.name}</code>{' '}
            <code className="text-foreground">v{manifest.serverInfo.version}</code>
          </p>
          {manifest.tools.map((tool) => (
            <ToolCard key={tool.name} tool={tool} />
          ))}
        </div>
      )}
    </Section>
  );
}

function ToolCard({ tool }: { tool: ManifestTool }) {
  const props = tool.inputSchema?.properties ?? {};
  const required = new Set(tool.inputSchema?.required ?? []);
  const paramRows = Object.entries(props);

  // Pre-cooked example body for the tools/call payload.
  const exampleArgs = useMemo(() => buildExampleArgs(tool.name, props, required), [tool.name, props, required]);
  const exampleJson = JSON.stringify(
    {
      jsonrpc: '2.0',
      id: 1,
      method: 'tools/call',
      params: { name: tool.name, arguments: exampleArgs },
    },
    null,
    2,
  );

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-border flex flex-wrap items-baseline justify-between gap-3 border-b p-5">
        <div>
          <code className="text-foreground text-base font-semibold">{tool.name}</code>
          <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{tool.description}</p>
        </div>
        <Badge variant="outline" className="border-border rounded-full text-[10px] font-medium uppercase tracking-wider">
          {required.size} required · {paramRows.length} total
        </Badge>
      </div>

      <div className="bg-muted/20 p-5">
        <p className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wider">
          Parameters
        </p>
        {paramRows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No parameters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-left text-xs">
                  <th className="pb-2 pr-3 font-medium">Name</th>
                  <th className="pb-2 pr-3 font-medium">Type</th>
                  <th className="pb-2 pr-3 font-medium">Required</th>
                  <th className="pb-2 font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {paramRows.map(([name, schema]) => (
                  <tr key={name} className="border-border border-b align-top last:border-0">
                    <td className="py-2.5 pr-3 font-mono text-xs">{name}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs">
                      {renderSchemaType(schema)}
                    </td>
                    <td className="py-2.5 pr-3 text-xs">
                      {required.has(name) ? (
                        <span className="text-foreground">yes</span>
                      ) : (
                        <span className="text-muted-foreground">no</span>
                      )}
                    </td>
                    <td className="text-muted-foreground py-2.5 text-xs leading-relaxed">
                      {schema.description || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border-border border-t p-5">
        <p className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wider">
          Example call
        </p>
        <CodeBlock language="json" code={exampleJson} />
      </div>
    </Card>
  );
}

function renderSchemaType(schema: JsonSchema): string {
  if (schema.enum && schema.enum.length > 0) {
    const sample = schema.enum.slice(0, 3).map((v) => JSON.stringify(v)).join(' | ');
    return schema.enum.length > 3 ? `${sample} | …` : sample;
  }
  if (schema.type === 'array' && schema.items) {
    return `${schema.items.type ?? 'any'}[]`;
  }
  return schema.type ?? 'any';
}

/**
 * Best-effort sample arguments for a tool. We pick values that are valid
 * against the schema (required fields populated; optional fields skipped)
 * so the example you copy is a working request.
 */
function buildExampleArgs(
  toolName: string,
  props: Record<string, JsonSchema>,
  required: Set<string>,
): Record<string, unknown> {
  // Tool-specific overrides feel more realistic than generic placeholders.
  const overrides: Record<string, Record<string, unknown>> = {
    draft_email: {
      prompt: 'Friday-only flash sale, 30% off all towels, sense of urgency.',
      style: 'bold',
    },
    modify_email: {
      draft_id: '01HZAB3CDE4FGH5IJK6LMN7P',
      modification_prompt: 'Shorten the headline and bump the offer to 20% off.',
    },
    screenshot_email: {
      draft_id: '01HZAB3CDE4FGH5IJK6LMN7P',
      width: 600,
      format: 'png',
    },
    send_email: {
      draft_id: '01HZAB3CDE4FGH5IJK6LMN7P',
      from_email: 'hello@yourdomain.com',
      from_name: 'Acme',
      to: ['alice@example.com'],
      subject: 'Welcome to Acme',
    },
    list_drafts: { status: 'draft', limit: 20 },
  };

  const override = overrides[toolName] ?? {};
  const out: Record<string, unknown> = {};
  for (const [name, schema] of Object.entries(props)) {
    if (name in override) {
      out[name] = override[name];
      continue;
    }
    if (!required.has(name)) continue;
    out[name] = placeholderFor(schema);
  }
  return out;
}

function placeholderFor(schema: JsonSchema): unknown {
  if (schema.enum && schema.enum.length > 0) return schema.enum[0];
  if (schema.type === 'string') return 'string';
  if (schema.type === 'integer' || schema.type === 'number') return schema.minimum ?? 1;
  if (schema.type === 'boolean') return false;
  if (schema.type === 'array') return [];
  if (schema.type === 'object') return {};
  return null;
}

// ---------------------------------------------------------------------------
// Section: REST
// ---------------------------------------------------------------------------

function RestSection() {
  return (
    <Section id="rest" title="REST endpoints">
      <p>
        Same data as MCP, exposed as plain JSON over HTTPS. Base URL{' '}
        <Inline>https://sendfast.ai</Inline>. Bearer auth on every request. Errors come back
        as <Inline>{`{ "error": "..." }`}</Inline> with the relevant 4xx / 5xx HTTP status.
      </p>

      <div className="mt-6 space-y-6">
        {REST_ENDPOINTS.map((group) => (
          <div key={group.group}>
            <h3 className="text-base font-semibold tracking-tight">{group.group}</h3>
            <div className="border-border bg-muted/20 mt-3 overflow-hidden rounded-lg border">
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {group.endpoints.map((ep) => (
                    <tr key={`${ep.method} ${ep.path}`} className="border-border border-b last:border-0">
                      <td className="py-3 pl-4 pr-3 align-middle">
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase',
                            METHOD_COLORS[ep.method],
                          )}
                        >
                          {ep.method}
                        </span>
                      </td>
                      <td className="py-3 pr-3 align-middle font-mono text-xs">{ep.path}</td>
                      <td className="text-muted-foreground py-3 pr-4 align-middle text-xs leading-relaxed">
                        {ep.summary}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <p className="text-muted-foreground mt-6 text-sm">
        Full request / response shapes for each endpoint will land in a dedicated reference page.
        For now, schemas are visible directly in the relevant{' '}
        <a
          href="https://github.com/sendfast/sendfast/tree/main/src/routes"
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          src/routes/*.ts
        </a>{' '}
        in the repo — every route uses{' '}
        <a
          href="https://zod.dev"
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          Zod
        </a>{' '}
        validation, so the parser definition <em>is</em> the schema.
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: Clients
// ---------------------------------------------------------------------------

function ClientsSection() {
  const claudeConfig = `{
  "mcpServers": {
    "sendfast": {
      "url": "https://sendfast.ai/api/mcp",
      "headers": {
        "Authorization": "Bearer sf_live_REPLACE_ME"
      }
    }
  }
}`;

  return (
    <Section id="clients" title="Client setup">
      <p>
        All MCP clients use the same JSON shape. Drop your API key in and restart the host —
        SendFast tools will appear in the tool picker.
      </p>

      <Card className="border-primary/30 bg-primary/[0.04] mt-6 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Skip the JSON — one-click install</p>
            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
              We mint an API key and hand it to your client over a deeplink. Cursor, VS Code,
              VS Code Insiders supported today; Claude Desktop and Claude.ai have a two-step
              setup on the install page.
            </p>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/install">
              Open installer <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
        <div className="mt-4">
          <InstallButtons />
        </div>
      </Card>

      <div className="mt-6 space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold tracking-tight">Claude Desktop</h3>
            <Badge variant="secondary" className="rounded-full text-[10px] font-medium">
              Recommended
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">
            Edit <Inline>~/Library/Application Support/Claude/claude_desktop_config.json</Inline>{' '}
            (macOS) or <Inline>%APPDATA%\Claude\claude_desktop_config.json</Inline> (Windows):
          </p>
          <CodeBlock language="json" code={claudeConfig} />
        </div>

        <div>
          <h3 className="text-base font-semibold tracking-tight">Cursor</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            Same JSON, edit <Inline>~/.cursor/mcp.json</Inline> (Cursor reads MCP servers from
            that file).
          </p>
          <CodeBlock language="json" code={claudeConfig} />
        </div>

        <div>
          <h3 className="text-base font-semibold tracking-tight">Custom agents</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            Any JSON-RPC client that speaks the MCP streamable-HTTP transport works — LangGraph,
            CrewAI, custom Node / Python clients, etc. The wire format is documented at{' '}
            <a
              href="https://modelcontextprotocol.io/specification"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              modelcontextprotocol.io/specification
            </a>
            .
          </p>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section: curl
// ---------------------------------------------------------------------------

function CurlSection() {
  return (
    <Section id="curl" title="curl recipes">
      <p>Copy-paste-runnable smoke tests. Swap in your real key + URL.</p>

      <div className="mt-6 space-y-6">
        <Recipe title="Discover the MCP manifest (no auth)">
          <CodeBlock
            language="bash"
            code={`curl https://sendfast.ai/api/mcp/manifest | jq .`}
          />
        </Recipe>

        <Recipe title="List tools (JSON-RPC)">
          <CodeBlock
            language="bash"
            code={`curl -X POST https://sendfast.ai/api/mcp \\
  -H 'Authorization: Bearer sf_live_xxx' \\
  -H 'Content-Type: application/json' \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}' | jq .`}
          />
        </Recipe>

        <Recipe title="Generate a draft (REST)">
          <CodeBlock
            language="bash"
            code={`curl -X POST https://sendfast.ai/api/generate \\
  -H 'Authorization: Bearer sf_live_xxx' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "prompt": "Friday-only flash sale, 30% off all towels, sense of urgency.",
    "style": "bold"
  }' | jq .`}
          />
        </Recipe>

        <Recipe title="Send a draft (REST)">
          <CodeBlock
            language="bash"
            code={`curl -X POST https://sendfast.ai/api/send \\
  -H 'Authorization: Bearer sf_live_xxx' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "draft_id": "01HZAB...",
    "from_email": "hello@yourdomain.com",
    "from_name": "Acme",
    "to": ["alice@example.com"],
    "subject": "Welcome to Acme"
  }' | jq .`}
          />
        </Recipe>
      </div>
    </Section>
  );
}

function Recipe({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wider">
        {title}
      </p>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Errors & limits
// ---------------------------------------------------------------------------

function ErrorsSection() {
  return (
    <Section id="errors" title="Errors & limits">
      <p>
        REST returns standard HTTP status codes with a JSON body containing{' '}
        <Inline>{`{ "error": string, "code"?: string }`}</Inline>. MCP wraps tool failures in a
        successful JSON-RPC response with <Inline>isError: true</Inline> so the agent can read
        the message; protocol-level problems use the standard JSON-RPC error envelope.
      </p>

      <div className="border-border bg-muted/20 mt-6 overflow-hidden rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-muted-foreground border-border border-b text-left text-xs">
              <th className="py-2 pl-4 pr-3 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Meaning</th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {[
              ['400', 'Validation failed — see the `error` field for the offending parameter.'],
              ['401', 'Missing or invalid API key. Check the Authorization header.'],
              ['402', 'Out of quota for your plan (sends / generations / edits).'],
              ['403', 'Authenticated but not allowed (e.g. unverified sending domain).'],
              ['404', 'Resource (draft, domain, key) does not exist or belongs to another user.'],
              ['429', 'Rate-limited. Slow down or retry with exponential backoff.'],
              ['5xx', 'We logged it. Safe to retry idempotently.'],
            ].map(([code, meaning]) => (
              <tr key={code} className="border-border border-b last:border-0 align-top">
                <td className="py-2.5 pl-4 pr-3 font-mono">{code}</td>
                <td className="text-muted-foreground py-2.5 pr-4 leading-relaxed">{meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-muted-foreground mt-6 text-sm">
        Limits scale with your plan. See{' '}
        <Link to="/app/settings?tab=plan" className="text-primary hover:underline">
          Settings → Plan
        </Link>{' '}
        for your current ceiling, or the public{' '}
        <a href="/#pricing" className="text-primary hover:underline">
          pricing page
        </a>{' '}
        for the comparison.
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    // scroll-mt accounts for the sticky header so anchor jumps don't land
    // with the heading hidden behind it.
    <section id={id} className="scroll-mt-20">
      <div className="flex items-baseline gap-2">
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <a
          href={`#${id}`}
          className="text-muted-foreground hover:text-foreground text-xs"
          aria-label={`Link to ${title}`}
        >
          #
        </a>
      </div>
      <div className="text-foreground/85 mt-4 max-w-3xl text-base leading-relaxed [&_p]:mb-3">
        {children}
      </div>
    </section>
  );
}

function Inline({ children }: { children: React.ReactNode }) {
  return (
    <code className="bg-muted text-foreground rounded px-1.5 py-0.5 font-mono text-[0.85em]">
      {children}
    </code>
  );
}

function CodeBlock({ code, language: _language }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <div className="bg-muted/50 border-border group relative my-4 overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={copy}
        className="text-muted-foreground hover:text-foreground hover:bg-background absolute right-2 top-2 flex items-center gap-1 rounded-md border border-border bg-background/80 px-2 py-1 text-[10px] font-medium uppercase tracking-wider opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
        aria-label="Copy"
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
        {copied ? 'copied' : 'copy'}
      </button>
      <pre className="overflow-x-auto px-4 py-3.5 font-mono text-xs leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

